import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { motion } from 'framer-motion';
import { Ban, X, Plus, Minus, Coins } from 'lucide-react';
import { Button } from '../common/Button';
import { Input } from '../common/Input';
import { User, GTWallet } from '../../types';
import { TestModeWarning } from '../common/TestModeBanner';

// Ban Modal
interface BanModalProps {
  user: User;
  onClose: () => void;
  onConfirm: (reason: string) => void;
}

export const BanModal: React.FC<BanModalProps> = ({ user, onClose, onConfirm }) => {
  const [reason, setReason] = useState('');

  return ReactDOM.createPortal(
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white dark:bg-surface-dark rounded-xl shadow-xl max-w-md w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 bg-error/10 rounded-full flex items-center justify-center">
            <Ban size={24} className="text-error" />
          </div>
          <div>
            <h3 className="text-xl font-bold">Ban User</h3>
            <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">@{user.username}</p>
          </div>
        </div>
        <TestModeWarning action="Banning this user" />
        <Input
          label="Ban Reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Enter reason for ban..."
        />
        <div className="flex gap-3 mt-6">
          <Button variant="secondary" onClick={onClose} className="flex-1">
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => onConfirm(reason)}
            disabled={!reason.trim()}
            className="flex-1"
          >
            Ban User
          </Button>
        </div>
      </motion.div>
    </motion.div>,
    document.body
  );
};

// Adjust GT Modal
interface AdjustGTModalProps {
  user: User;
  wallet: GTWallet | undefined;
  onClose: () => void;
  onConfirm: (amount: number, description: string) => void;
}

export const AdjustGTModal: React.FC<AdjustGTModalProps> = ({
  user,
  wallet,
  onClose,
  onConfirm
}) => {
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [isAdding, setIsAdding] = useState(true);

  const handleConfirm = () => {
    const numAmount = parseInt(amount) * (isAdding ? 1 : -1);
    onConfirm(numAmount, description);
  };

  return ReactDOM.createPortal(
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white dark:bg-surface-dark rounded-xl shadow-xl max-w-md w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold">Adjust GT Balance</h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-surface-light dark:hover:bg-gray-800 rounded-full"
          >
            <X size={20} />
          </button>
        </div>

        <TestModeWarning action="Adjusting GT balance" />

        <div className="bg-surface-light dark:bg-gray-800 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-3">
            <img src={user.photoURL} alt={user.username} className="w-10 h-10 rounded-full" />
            <div>
              <p className="font-medium">@{user.username}</p>
              <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                Current: {wallet?.balance.toLocaleString() ?? 0} GT
              </p>
            </div>
          </div>
        </div>

        <div className="flex gap-2 mb-4">
          <Button
            variant={isAdding ? 'primary' : 'secondary'}
            onClick={() => setIsAdding(true)}
            icon={<Plus size={18} />}
            className="flex-1"
          >
            Add
          </Button>
          <Button
            variant={!isAdding ? 'primary' : 'secondary'}
            onClick={() => setIsAdding(false)}
            icon={<Minus size={18} />}
            className="flex-1"
          >
            Remove
          </Button>
        </div>

        <div className="space-y-4">
          <Input
            label="Amount"
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="Enter GT amount"
            icon={<Coins size={18} />}
          />
          <Input
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Reason for adjustment..."
          />
        </div>

        <div className="flex gap-3 mt-6">
          <Button variant="secondary" onClick={onClose} className="flex-1">
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirm}
            disabled={!amount || !description.trim()}
            className="flex-1"
          >
            Confirm
          </Button>
        </div>
      </motion.div>
    </motion.div>,
    document.body
  );
};
